import React from 'react';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Scale, 
  Shield, 
  Heart,
  Award,
  MessageCircle,
  ChevronRight,
  Building,
  Clock,
  Gavel
} from 'lucide-react';
import './App.css';

// Imagem otimizada sem lazy loading para elementos críticos
const OptimizedImage = ({ src, alt, className, ...props }) => {
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      loading="eager"
      decoding="async"
      {...props}
    />
  );
};

function App() {
  const areasAtuacao = [
    {
      titulo: 'Direito Cível',
      icon: Building,
      itens: ['Inventários', 'Usucapião', 'Regularização de Bens', 'Divórcio'],
      cor: 'bg-gradient-to-br from-stone-800 to-stone-900 border-amber-700/30 text-amber-50 hover:border-amber-500 shadow-2xl'
    },
    {
      titulo: 'Direito Previdenciário',
      icon: Shield,
      itens: ['Aposentadorias', 'Benefícios INSS', 'Direitos Previdenciários'],
      cor: 'bg-gradient-to-br from-stone-800 to-stone-900 border-amber-700/30 text-amber-50 hover:border-amber-500 shadow-2xl'
    },
    {
      titulo: 'Direito Penal',
      icon: Gavel,
      itens: ['Defesa Criminal', 'Acompanhamento Processual'],
      cor: 'bg-gradient-to-br from-stone-800 to-stone-900 border-amber-700/30 text-amber-50 hover:border-amber-500 shadow-2xl'
    }
  ];

  const diferenciais = [
    {
      icon: Heart,
      titulo: 'Atendimento Humanizado',
      descricao: 'Foco no cliente com empatia e dedicação'
    },
    {
      icon: Award,
      titulo: 'Ética Profissional',
      descricao: 'Conduta ética e responsabilidade em cada caso'
    },
    {
      icon: Scale,
      titulo: 'Transparência Total',
      descricao: 'Comunicação clara e honesta em todo processo'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-900 via-amber-950/20 to-stone-900">
      {/* Hero Section - altura ajustada para mobile */}
      <section className="relative min-h-[85vh] md:min-h-screen flex items-center justify-center overflow-hidden py-12 md:py-20">
        <div className="absolute inset-0 bg-gradient-to-r from-stone-900/95 via-amber-900/40 to-stone-900/95"></div>
        
        {/* Background Image - carregamento otimizado */}
        <div className="absolute inset-0">
          <img 
            src="/dra-maria-darc.jpg?v=3" 
            alt="" 
            className="w-full h-full object-cover opacity-15"
            loading="eager"
            decoding="async"
          />
        </div>
        
        {/* Roman Pattern Overlay */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(212, 175, 55, 0.1) 35px, rgba(212, 175, 55, 0.1) 70px)`,
          }}></div>
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-6 py-12 md:py-20 text-center">
          <div className="mb-6 md:mb-8">
            <div className="inline-flex items-center justify-center mb-4 md:mb-8 p-4 md:p-6 bg-gradient-to-b from-stone-800/80 to-stone-900/80 rounded-2xl border border-amber-600/30 shadow-2xl shadow-amber-900/20 backdrop-blur-sm">
              <OptimizedImage 
                src="/logo-transparente.jpg" 
                alt="Dra. Mária D'arc - Advocacia" 
                className="h-40 md:h-48 w-auto max-w-xs md:max-w-md object-contain drop-shadow-lg"
              />
            </div>
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-amber-50 mb-4 md:mb-6 leading-tight">
            Advocacia
            <span className="block text-amber-400">Humanizada</span>
          </h1>

          <p className="text-lg md:text-xl lg:text-2xl text-amber-100/90 mb-6 md:mb-8 max-w-4xl mx-auto leading-relaxed px-2">
            Dra. Mária D'arc é advogada comprometida com a defesa dos direitos de seus clientes, 
            atuando com ética, responsabilidade e dedicação.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center">
            <a
              href="https://wa.me/5565992017542"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary flex items-center gap-2 bg-green-600 hover:bg-green-700 text-sm md:text-base px-4 md:px-6 py-3"
            >
              <MessageCircle className="w-5 h-5" />
              Agendar Consultoria
            </a>
          </div>
        </div>
      </section>

      {/* Áreas de Atuação */}
      <section className="py-12 md:py-20 px-4 md:px-6 bg-gradient-to-b from-stone-800 to-stone-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-amber-50 mb-4">
              Áreas de <span className="text-amber-400">Atuação</span>
            </h2>
            <p className="text-lg md:text-xl text-amber-100/80 max-w-3xl mx-auto">
              Atuação especializada com foco em soluções eficazes para suas necessidades jurídicas
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {areasAtuacao.map((area, index) => (
              <div
                key={index}
                className={`card-hover border-2 rounded-2xl p-6 md:p-8 ${area.cor}`}
              >
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-900 to-stone-700 rounded-full flex items-center justify-center mr-4 border border-amber-600/50">
                    <area.icon className="w-8 h-8 text-amber-400" />
                  </div>
                  <h3 className="text-2xl font-bold">{area.titulo}</h3>
                </div>
                <ul className="space-y-3">
                  {area.itens.map((item, idx) => (
                    <li key={idx} className="flex items-center">
                      <ChevronRight className="w-5 h-5 mr-2 flex-shrink-0" />
                      <span className="text-lg">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Diferenciais */}
      <section className="py-12 md:py-20 px-4 md:px-6 bg-gradient-to-b from-stone-900 via-amber-950/10 to-stone-800">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-amber-50 mb-4">
              Nossos <span className="text-amber-400">Diferenciais</span>
            </h2>
            <p className="text-lg md:text-xl text-amber-100/80 max-w-3xl mx-auto">
              Valores que guiam nossa prática profissional
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {diferenciais.map((diferencial, index) => (
              <div
                key={index}
                className="text-center group"
              >
                <div className="w-20 h-20 bg-gradient-to-br from-stone-800 to-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:from-amber-900/50 group-hover:to-stone-700 transition-all duration-300 border border-amber-700/30 shadow-xl">
                  <diferencial.icon className="w-10 h-10 text-amber-400" />
                </div>
                <h3 className="text-2xl font-bold text-amber-50 mb-4">
                  {diferencial.titulo}
                </h3>
                <p className="text-amber-100/80 leading-relaxed">
                  {diferencial.descricao}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sobre Completo */}
      <section className="py-12 md:py-20 px-4 md:px-6 bg-gradient-to-b from-stone-800 to-stone-900">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Foto da Dra. Mária D'arc */}
            <div className="relative order-2 md:order-1">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-2 border-amber-700/30 max-w-sm mx-auto md:max-w-none">
                <img 
                  src="/dra-maria-darc.jpg?v=3" 
                  alt="Dra. Mária D'arc" 
                  className="w-full h-full object-cover aspect-[3/4]"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/40 to-transparent"></div>
              </div>
              <div className="absolute -bottom-3 -right-3 md:-bottom-4 md:-right-4 w-16 h-16 md:w-24 md:h-24 bg-gradient-to-br from-amber-600 to-amber-800 rounded-full flex items-center justify-center shadow-2xl border-2 border-amber-500">
                <Scale className="w-8 h-8 md:w-12 md:h-12 text-stone-900" />
              </div>
            </div>

            {/* Conteúdo Sobre */}
            <div className="order-1 md:order-2">
              <h2 className="text-4xl md:text-5xl font-bold text-amber-50 mb-8">
                Sobre <span className="text-amber-400">Dra. Mária D'arc</span>
              </h2>
              
              <div className="space-y-6 text-lg text-amber-100/90 leading-relaxed mb-8">
                <p>
                  Dra. Mária D'arc é advogada comprometida com a defesa dos direitos de seus clientes, 
                  atuando com ética, responsabilidade e dedicação. Oferece orientação jurídica clara e 
                  acompanhamento profissional em diversas demandas judiciais e extrajudiciais.
                </p>
                
                <p>
                  Possui atuação na área Cível, com foco em inventários, usucapião e regularização de bens, 
                  além de atuar também na área Previdenciária, auxiliando clientes na busca por benefícios 
                  previdenciários, aposentadorias e demais direitos perante o INSS.
                </p>
                
                <p>
                  Seu trabalho é pautado no atendimento humanizado, na análise cuidadosa de cada caso e na 
                  busca por soluções jurídicas seguras e eficazes, sempre com transparência e comprometimento 
                  com a justiça.
                </p>
              </div>

              <div className="mt-6">
                <a
                  href="https://wa.me/5565992017542"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary bg-green-600 hover:bg-green-700 inline-flex items-center gap-2"
                >
                  <MessageCircle className="w-5 h-5" />
                  Fale Conosco via WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-b from-stone-950 to-stone-900 text-amber-50 py-8 md:py-12 px-4 md:px-6 border-t border-amber-900/30">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center mb-4">
                <img 
                  src="/logo-transparente.jpg" 
                  alt="Dra. Mária D'arc - Advocacia" 
                  className="h-12 w-auto mr-3 object-contain"
                  loading="lazy"
                />
                <h3 className="text-xl font-bold">Dra. Mária D'arc</h3>
              </div>
              <p className="text-amber-100/80">
                Advocacia humanizada e comprometida com seus direitos
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Contato</h4>
              <div className="space-y-2 text-amber-100/80">
                <div className="flex items-center">
                  <Phone className="w-4 h-4 mr-2" />
                  <span>(65) 9 9201-7542</span>
                </div>
                <div className="flex items-center">
                  <Mail className="w-4 h-4 mr-2" />
                  <span>mdarcadv@outlook.com</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 mr-2" />
                  <span>Nobres, Mato Grosso</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Horário de Atendimento</h4>
              <div className="space-y-2 text-amber-100/80">
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-2" />
                  <span>Segunda a Sexta: 9h às 18h</span>
                </div>
                <div className="flex items-center">
                  <Gavel className="w-4 h-4 mr-2" />
                  <span>Sábado: 9h às 12h</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-amber-900/30 pt-8 text-center text-amber-200/60">
            <p>&copy; 2024 Dra. Mária D'arc. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
